import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../components/admin/AdminLayout';
import LoginSecurityTab from '../../components/admin/LoginSecurityTab';
import {
  UserCheck, Mail, Briefcase, Calendar, FileText, Pause, Play,
  CreditCard as Edit, Key, UserPlus, X, Check, AlertCircle,
  ClipboardCheck, Download, ChevronUp, ChevronDown,
  MoreVertical, Trash2, Trash, RotateCcw, AlertTriangle, CheckCircle, Shield,
  Users, MessageSquare, Heart, Eye, Globe, Lock, TrendingUp, Phone,
  MapPin, DollarSign, Activity
} from 'lucide-react';

interface ConsultantStats {
  totalPosts: number;
  publicPosts: number;
  privatePosts: number;
  hiddenPosts: number;
  totalLikes: number;
  totalComments: number;
  totalFollowers: number;
  totalFollowing: number;
  totalConnections: number;
  pendingConnections: number;
  groupChats: number;
  directMessageThreads: number;
  lastActive: string | null;
}

const COMPLETION_FIELDS = [
  'first_name', 'last_name', 'email', 'title',
  'country_region', 'experience_years', 'workday_modules',
  'resume_url', 'photo_url', 'experience_text'
] as const;

function computeConsultantCompleteness(row: any): number {
  const filled = COMPLETION_FIELDS.filter(f => row[f] && row[f].toString().trim() !== '').length;
  return Math.round((filled / COMPLETION_FIELDS.length) * 100);
}

interface ConsultantProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  company: string | null;
  title: string | null;
  country_region: string | null;
  experience_years: string | null;
  workday_modules: string | null;
  certifications: string | null;
  availability: string | null;
  hourly_rate: string | null;
  message: string | null;
  profile_complete: boolean | null;
  resume_url: string | null;
  status: string | null;
  project_preferences: string | null;
  profile_photo_url: string | null;
  project_end_date: string | null;
  photo_url: string | null;
  created_at: string;
  updated_at: string | null;
  full_name?: string;
}

interface UserData {
  email: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
}

interface ConsultantWithUser extends ConsultantProfile {
  soft_deleted_at?: string | null;
  soft_deleted_by?: string | null;
  user_data?: UserData;
  account_status?: {
    status: string;
    paused_reason?: string;
    paused_at?: string;
  };
  active_assignment?: {
    id: string;
    client_id: string;
    start_date: string;
    end_date?: string;
    status: string;
    hourly_rate: number;
    contract_url?: string;
    client_name?: string;
    company_name?: string;
  };
  deletion_request?: {
    id: string;
    status: string;
    reason?: string;
    requested_at: string;
  } | null;
}

interface Client {
  id: string;
  full_name: string;
  email: string;
  company_id?: string;
}


interface AssignmentModalData {
  consultant_id: string;
  consultant_name: string;
  client_id: string;
  start_date: string;
  hourly_rate: string;
  notes: string;
  contract_file: File | null;
}

type SortField = 'name' | 'email' | 'location' | 'experience' | 'availability' | 'rate' | 'joined' | 'assignment';
type SortDir = 'asc' | 'desc';

function exportToCSV(consultants: ConsultantWithUser[]) {
  const headers = [
    'Name', 'Email', 'Phone', 'Location', 'Title',
    'Experience (yrs)', 'Workday Modules', 'Certifications',
    'Hourly Rate', 'Availability', 'Assignment', 'Client',
    'Account Status', 'Joined'
  ];

  const rows = consultants.map(c => [
    c.full_name || `${c.first_name} ${c.last_name}`,
    c.email,
    c.phone || '',
    c.country_region || '',
    c.title || '',
    c.experience_years || '',
    c.workday_modules || '',
    c.certifications || '',
    c.hourly_rate ? (c.hourly_rate.toString().startsWith('$') ? c.hourly_rate : `$${c.hourly_rate}`) : '',
    c.active_assignment ? 'On Assignment' : (c.availability || ''),
    c.active_assignment ? 'Active' : '',
    c.active_assignment?.client_name || '',
    c.account_status?.status === 'paused' ? 'Paused' : 'Active',
    c.created_at ? new Date(c.created_at).toLocaleDateString() : ''
  ]);

  const csv = [headers, ...rows]
    .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `workday-consultants-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportToPDF(consultants: ConsultantWithUser[]) {
  const rows = consultants.map(c => `
    <tr>
      <td>${c.full_name || `${c.first_name} ${c.last_name}`}</td>
      <td>${c.email}</td>
      <td>${c.phone || ''}</td>
      <td>${c.country_region || ''}</td>
      <td>${c.experience_years || ''}</td>
      <td>${c.workday_modules || ''}</td>
      <td>${c.hourly_rate ? (c.hourly_rate.toString().startsWith('$') ? c.hourly_rate : `$${c.hourly_rate}`) : ''}</td>
      <td>${c.active_assignment ? `On Assignment (${c.active_assignment.client_name || ''})` : (c.availability || '')}</td>
      <td>${c.account_status?.status === 'paused' ? 'Paused' : 'Active'}</td>
      <td>${c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</td>
    </tr>
  `).join('');

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <title>Workday Consultants</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 10px; margin: 20px; }
        h1 { font-size: 16px; margin-bottom: 4px; color: #111; }
        p { font-size: 10px; color: #555; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #40E0D0; color: #111; font-weight: 600; padding: 6px 8px; text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; }
        td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; color: #374151; vertical-align: top; }
        tr:nth-child(even) td { background: #f9fafb; }
      </style>
    </head>
    <body>
      <h1>Workday Consultants</h1>
      <p>Generated ${new Date().toLocaleString()} &mdash; ${consultants.length} consultant(s)</p>
      <table>
        <thead>
          <tr>
            <th>Name</th><th>Email</th><th>Phone</th><th>Location</th>
            <th>Exp.</th><th>Modules</th><th>Rate</th>
            <th>Availability / Assignment</th><th>Account</th><th>Joined</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </body>
    </html>
  `;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 500);
}

export default function WorkdayConsultants() {
  const navigate = useNavigate();
  const [pageTab, setPageTab] = useState<'consultants' | 'login-security'>('consultants');
  const [consultants, setConsultants] = useState<ConsultantWithUser[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState<ConsultantWithUser | null>(null);
  const [assignmentData, setAssignmentData] = useState<AssignmentModalData>({
    consultant_id: '',
    consultant_name: '',
    client_id: '',
    start_date: new Date().toISOString().split('T')[0],
    hourly_rate: '',
    notes: '',
    contract_file: null,
  });
  const [editData, setEditData] = useState<Partial<ConsultantProfile>>({});
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    type: 'soft_delete' | 'hard_delete' | 'restore';
    consultant: ConsultantWithUser;
  } | null>(null);
  const [consultantStats, setConsultantStats] = useState<Record<string, ConsultantStats>>({});
  const [loadingStats, setLoadingStats] = useState<Record<string, boolean>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  const profileCompletionStats = React.useMemo(() => {
    let at80OrLess = 0;
    let at90 = 0;
    let at100 = 0;
    for (const c of consultants) {
      const pct = computeConsultantCompleteness(c);
      if (pct === 100) at100++;
      else if (pct >= 90) at90++;
      else at80OrLess++;
    }
    return { at80OrLess, at90, at100, total: consultants.length };
  }, [consultants]);

  useEffect(() => {
    checkAuth();
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
        setDropdownPos(null);
      }
    };
    if (openDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) navigate('/admin/login');
  };

  const fetchData = async () => {
    try {
      setError(null);
      await Promise.all([fetchConsultants(), fetchClients()]);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultants = async () => {
    const { data: profiles, error: profilesError } = await supabase
      .from('consultant_profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (profilesError) throw profilesError;
    if (!profiles || profiles.length === 0) { setConsultants([]); return; }

    const consultantIds = profiles.map(p => p.id);

    const [statusResult, assignmentsResult, deletionResult] = await Promise.all([
      supabase.from('consultant_account_status').select('consultant_id, status, paused_reason, paused_at').in('consultant_id', consultantIds),
      supabase.from('consultant_assignments').select('id, consultant_id, client_id, client_name, start_date, end_date, status, hourly_rate, contract_url').in('consultant_id', consultantIds).eq('status', 'active'),
      supabase.from('account_deletion_requests').select('id, user_id, status, reason, requested_at').in('user_id', consultantIds).eq('user_type', 'consultant').eq('status', 'pending')
    ]);

    const statuses = statusResult.data || [];
    const assignments = assignmentsResult.data || [];
    const deletionRequests = deletionResult.data || [];

    const clientIds = assignments.map(a => a.client_id).filter(Boolean);

    let clientsData: any[] = [];

    if (clientIds.length > 0) {
      const { data } = await supabase.from('workday_client_profiles').select('id, first_name, last_name, email').in('id', clientIds);
      clientsData = (data || []).map((c: any) => ({ ...c, full_name: `${c.first_name} ${c.last_name}`.trim() }));
    }

    const enrichedProfiles = profiles.map(profile => {
      const status = statuses.find(s => s.consultant_id === profile.id);
      const assignment = assignments.find(a => a.consultant_id === profile.id);
      let enrichedAssignment = undefined;
      if (assignment) {
        enrichedAssignment = {
          ...assignment,
          client_name: clientsData.find((c: any) => c.id === assignment.client_id)?.full_name,
          company_name: assignment.client_name || undefined
        };
      }
      const deletionReq = deletionRequests.find((d: any) => d.user_id === profile.id);
      return {
        ...profile,
        full_name: `${profile.first_name} ${profile.last_name}`,
        user_data: { email: profile.email, created_at: profile.created_at, first_name: profile.first_name, last_name: profile.last_name },
        account_status: status,
        active_assignment: enrichedAssignment,
        deletion_request: deletionReq || null
      };
    });

    setConsultants(enrichedProfiles);
  };

  const fetchClients = async () => {
    const { data, error } = await supabase.from('workday_client_profiles').select('id, first_name, last_name, email, company_id').order('first_name');
    if (error) throw error;
    setClients((data || []).map((c: any) => ({ ...c, full_name: `${c.first_name} ${c.last_name}`.trim() })));
  };

  const fetchConsultantStats = async (consultantId: string) => {
    if (consultantStats[consultantId] || loadingStats[consultantId]) return;
    setLoadingStats(prev => ({ ...prev, [consultantId]: true }));
    try {
      const [
        postsRes,
        followersRes,
        followingRes,
        connectionsRes,
        groupChatsRes,
        loginRes,
      ] = await Promise.all([
        supabase.from('consultant_community_posts').select('id, visibility, is_hidden, likes_count, comments_count, created_at').eq('consultant_id', consultantId),
        supabase.from('community_followers').select('id', { count: 'exact', head: true }).eq('following_id', consultantId),
        supabase.from('community_followers').select('id', { count: 'exact', head: true }).eq('follower_id', consultantId),
        supabase.from('consultant_connections').select('id, status').or(`consultant_id.eq.${consultantId},connected_consultant_id.eq.${consultantId}`),
        supabase.from('group_chat_members').select('id', { count: 'exact', head: true }).eq('user_id', consultantId),
        supabase.from('login_records').select('created_at').eq('user_id', consultantId).eq('success', true).order('created_at', { ascending: false }).limit(1),
      ]);

      const posts = postsRes.data || [];
      const connections = connectionsRes.data || [];

      const stats: ConsultantStats = {
        totalPosts: posts.length,
        publicPosts: posts.filter(p => p.visibility === 'public' && !p.is_hidden).length,
        privatePosts: posts.filter(p => p.visibility === 'connections_only' || p.visibility === 'private').length,
        hiddenPosts: posts.filter(p => p.is_hidden).length,
        totalLikes: posts.reduce((sum, p) => sum + (p.likes_count || 0), 0),
        totalComments: posts.reduce((sum, p) => sum + (p.comments_count || 0), 0),
        totalFollowers: followersRes.count || 0,
        totalFollowing: followingRes.count || 0,
        totalConnections: connections.filter(c => c.status === 'accepted').length,
        pendingConnections: connections.filter(c => c.status === 'pending').length,
        groupChats: groupChatsRes.count || 0,
        directMessageThreads: 0,
        lastActive: loginRes.data?.[0]?.created_at || null,
      };

      setConsultantStats(prev => ({ ...prev, [consultantId]: stats }));
    } catch {
      // silently fail — stats are supplementary
    } finally {
      setLoadingStats(prev => ({ ...prev, [consultantId]: false }));
    }
  };

  const handlePauseAccount = async (consultantId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'paused' ? 'active' : 'paused';
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('consultant_account_status').upsert({
        consultant_id: consultantId, status: newStatus,
        paused_at: newStatus === 'paused' ? new Date().toISOString() : null,
        paused_by: newStatus === 'paused' ? user?.id : null,
        paused_reason: newStatus === 'paused' ? 'Paused by admin' : null,
        updated_at: new Date().toISOString()
      });
      if (error) throw error;
      showMessage('success', `Account ${newStatus === 'paused' ? 'paused' : 'activated'} successfully`);
      await fetchConsultants();
    } catch {
      showMessage('error', 'Failed to update account status');
    }
  };

  const handleSendPasswordReset = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/consultant/login` });
      if (error) throw error;
      showMessage('success', `Password reset email sent to ${email}`);
    } catch {
      showMessage('error', 'Failed to send password reset email');
    }
  };

  const openAssignModal = (consultant: ConsultantWithUser) => {
    setSelectedConsultant(consultant);
    setAssignmentData({
      consultant_id: consultant.id,
      consultant_name: consultant.full_name || `${consultant.first_name} ${consultant.last_name}`,
      client_id: '',
      start_date: new Date().toISOString().split('T')[0],
      hourly_rate: consultant.hourly_rate || '',
      notes: '', contract_file: null,
    });
    setShowAssignModal(true);
  };

  const openEditModal = (consultant: ConsultantWithUser) => {
    setSelectedConsultant(consultant);
    setEditData({
      first_name: consultant.first_name, last_name: consultant.last_name,
      email: consultant.email, phone: consultant.phone,
      country_region: consultant.country_region, hourly_rate: consultant.hourly_rate,
      availability: consultant.availability, message: consultant.message,
    });
    setShowEditModal(true);
  };

  const handleCreateAssignment = async () => {
    if (!assignmentData.client_id || !assignmentData.start_date) { showMessage('error', 'Please select a client and start date'); return; }
    setProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const selectedClient = clients.find(c => c.id === assignmentData.client_id);
      let contractUrl = null;
      if (assignmentData.contract_file) {
        const fileExt = assignmentData.contract_file.name.split('.').pop();
        const fileName = `${assignmentData.consultant_id}_${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage.from('consultant_contracts').upload(fileName, assignmentData.contract_file);
        if (uploadError) throw uploadError;
        const { data: { publicUrl } } = supabase.storage.from('consultant_contracts').getPublicUrl(fileName);
        contractUrl = publicUrl;
      }
      const { error: assignmentError } = await supabase.from('consultant_assignments').insert({
        consultant_id: assignmentData.consultant_id, client_id: assignmentData.client_id,
        start_date: assignmentData.start_date, status: 'active',
        hourly_rate: parseFloat(assignmentData.hourly_rate) || null,
        contract_url: contractUrl, notes: assignmentData.notes || null, created_by: user?.id
      });
      if (assignmentError) throw assignmentError;
      showMessage('success', 'Assignment created successfully');
      setShowAssignModal(false);
      await fetchConsultants();
    } catch {
      showMessage('error', 'Failed to create assignment');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to remove this assignment?')) return;
    try {
      const { error } = await supabase.from('consultant_assignments').update({ status: 'completed', end_date: new Date().toISOString() }).eq('id', assignmentId);
      if (error) throw error;
      showMessage('success', 'Assignment removed successfully');
      await fetchConsultants();
    } catch {
      showMessage('error', 'Failed to remove assignment');
    }
  };

  const handleUpdateConsultant = async () => {
    if (!selectedConsultant) return;
    setProcessing(true);
    try {
      const { error } = await supabase.from('consultant_profiles').update(editData).eq('id', selectedConsultant.id);
      if (error) throw error;
      showMessage('success', 'Consultant updated successfully');
      setShowEditModal(false);
      await fetchConsultants();
    } catch {
      showMessage('error', 'Failed to update consultant');
    } finally {
      setProcessing(false);
    }
  };

  const handleSoftDelete = async (consultant: ConsultantWithUser) => {
    setProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('consultant_profiles').update({
        soft_deleted_at: new Date().toISOString(),
        soft_deleted_by: user?.id,
      }).eq('id', consultant.id);
      if (error) throw error;
      await supabase.from('consultant_account_status').upsert({
        consultant_id: consultant.id,
        status: 'paused',
        paused_at: new Date().toISOString(),
        paused_by: user?.id,
        paused_reason: 'Account soft deleted — pending permanent removal',
        updated_at: new Date().toISOString(),
      });
      showMessage('success', 'Account soft deleted. User cannot log in. Data held for 30 days.');
      setConfirmModal(null);
      await fetchConsultants();
    } catch {
      showMessage('error', 'Failed to soft delete account');
    } finally {
      setProcessing(false);
    }
  };

  const handleHardDelete = async (consultant: ConsultantWithUser) => {
    setProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-delete-consultant`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ consultant_id: consultant.id, auth_user_id: consultant.id }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      showMessage('success', 'Account permanently deleted.');
      setConfirmModal(null);
      await fetchConsultants();
    } catch (err: any) {
      showMessage('error', err.message || 'Failed to permanently delete account');
    } finally {
      setProcessing(false);
    }
  };

  const handleRestore = async (consultant: ConsultantWithUser) => {
    setProcessing(true);
    try {
      const { error } = await supabase.from('consultant_profiles').update({
        soft_deleted_at: null,
        soft_deleted_by: null,
      }).eq('id', consultant.id);
      if (error) throw error;
      await supabase.from('consultant_account_status').upsert({
        consultant_id: consultant.id,
        status: 'active',
        paused_at: null,
        paused_by: null,
        paused_reason: null,
        updated_at: new Date().toISOString(),
      });
      showMessage('success', 'Account restored. User can now log in.');
      setConfirmModal(null);
      await fetchConsultants();
    } catch {
      showMessage('error', 'Failed to restore account');
    } finally {
      setProcessing(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const getSortValue = (c: ConsultantWithUser, field: SortField): string => {
    switch (field) {
      case 'name': return c.full_name || `${c.first_name} ${c.last_name}`;
      case 'email': return c.email || '';
      case 'location': return c.country_region || '';
      case 'experience': return c.experience_years || '';
      case 'availability': return c.active_assignment ? 'on assignment' : (c.availability || '');
      case 'rate': return c.hourly_rate?.replace(/[$,]/g, '') || '0';
      case 'joined': return c.created_at || '';
      case 'assignment': return c.active_assignment?.company_name || '';
      default: return '';
    }
  };

  const filteredConsultants = consultants
    .filter(c => {
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (c.full_name || `${c.first_name} ${c.last_name}`).toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.workday_modules || '').toLowerCase().includes(q) ||
        (c.country_region || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const va = getSortValue(a, sortField);
      const vb = getSortValue(b, sortField);
      const cmp = sortField === 'rate'
        ? (parseFloat(va) || 0) - (parseFloat(vb) || 0)
        : va.localeCompare(vb);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp className="h-3 w-3 text-gray-300" />;
    return sortDir === 'asc'
      ? <ChevronUp className="h-3 w-3 text-[#40E0D0]" />
      : <ChevronDown className="h-3 w-3 text-[#40E0D0]" />;
  };

  const thClass = "px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 transition-colors";

  const availabilityBadge = (c: ConsultantWithUser) => {
    if (c.active_assignment) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">On Assignment</span>;
    if (c.availability === 'available') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Available</span>;
    if (c.availability === 'busy') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Busy</span>;
    if (c.availability === 'unavailable') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Unavailable</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">N/A</span>;
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="max-w-full mx-auto">
          <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">Workday Consultants</h1>
              <p className="text-gray-500 text-sm">Manage and view all registered Workday consultants</p>
            </div>
            <button
              onClick={() => navigate('/admin/consultant-onboarding')}
              className="px-5 py-2.5 bg-[#40E0D0] text-white rounded-lg hover:bg-[#40E0D0]/90 font-semibold flex items-center gap-2 text-sm"
            >
              <ClipboardCheck className="h-4 w-4" />
              Onboarding
            </button>
          </div>

          {/* Page-level tab navigation */}
          <div className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <nav className="flex border-b border-gray-200 px-6" aria-label="Page tabs">
              <button
                onClick={() => setPageTab('consultants')}
                className={`py-4 px-1 mr-8 inline-flex items-center border-b-2 font-medium text-sm transition-colors ${
                  pageTab === 'consultants'
                    ? 'border-[#40E0D0] text-[#40E0D0]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Consultants
              </button>
              <button
                onClick={() => setPageTab('login-security')}
                className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm transition-colors ${
                  pageTab === 'login-security'
                    ? 'border-[#40E0D0] text-[#40E0D0]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Shield className="w-4 h-4 mr-2" />
                Login Security
              </button>
            </nav>
          </div>

          {pageTab === 'login-security' && (
            <LoginSecurityTab userType="consultant" />
          )}

          {pageTab === 'consultants' && !loading && consultants.length > 0 && (
            <div className="mb-5 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <h2 className="text-sm font-semibold text-gray-900">Profile Completion Overview</h2>
                <span className="text-xs text-gray-400 ml-1">({profileCompletionStats.total} total)</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">80% or Below</p>
                    <p className="text-2xl font-bold text-red-700 mt-0.5">{profileCompletionStats.at80OrLess}</p>
                    <p className="text-xs text-red-500 mt-0.5">
                      {profileCompletionStats.total > 0
                        ? `${Math.round((profileCompletionStats.at80OrLess / profileCompletionStats.total) * 100)}% of consultants`
                        : '0%'}
                    </p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-red-300" />
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">90% Complete</p>
                    <p className="text-2xl font-bold text-amber-700 mt-0.5">{profileCompletionStats.at90}</p>
                    <p className="text-xs text-amber-500 mt-0.5">
                      {profileCompletionStats.total > 0
                        ? `${Math.round((profileCompletionStats.at90 / profileCompletionStats.total) * 100)}% of consultants`
                        : '0%'}
                    </p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-amber-300" />
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">100% Complete</p>
                    <p className="text-2xl font-bold text-emerald-700 mt-0.5">{profileCompletionStats.at100}</p>
                    <p className="text-xs text-emerald-500 mt-0.5">
                      {profileCompletionStats.total > 0
                        ? `${Math.round((profileCompletionStats.at100 / profileCompletionStats.total) * 100)}% of consultants`
                        : '0%'}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-emerald-300" />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">Consultants at 90%+ are interview-eligible. Those at 80% or below need profile updates.</p>
            </div>
          )}

          {pageTab === 'consultants' && message && (
            <div className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {message.type === 'success' ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              <span className="text-sm">{message.text}</span>
            </div>
          )}

          {pageTab === 'consultants' && error && (
            <div className="mb-4 p-4 rounded-lg flex items-center gap-2 bg-red-50 text-red-800 border border-red-200">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {pageTab === 'consultants' && <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by name, email, module, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-sm text-gray-900 bg-white"
                />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm text-gray-500 mr-1">{filteredConsultants.length} consultants</span>
                <button
                  onClick={() => exportToCSV(filteredConsultants)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Download as Excel/CSV"
                >
                  <Download className="h-4 w-4 text-green-600" />
                  Excel
                </button>
                <button
                  onClick={() => exportToPDF(filteredConsultants)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Download as PDF"
                >
                  <Download className="h-4 w-4 text-red-500" />
                  PDF
                </button>
              </div>
            </div>

            {loading ? (
              <div className="p-12 text-center text-gray-500 text-sm">Loading consultants...</div>
            ) : filteredConsultants.length === 0 ? (
              <div className="p-12 text-center text-gray-500 text-sm">
                {searchTerm ? 'No consultants match your search.' : 'No consultants registered yet.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={thClass} onClick={() => handleSort('name')}>
                        <span className="flex items-center gap-1">Name <SortIcon field="name" /></span>
                      </th>
                      <th className={thClass} onClick={() => handleSort('email')}>
                        <span className="flex items-center gap-1">Email <SortIcon field="email" /></span>
                      </th>
                      <th className={thClass} onClick={() => handleSort('location')}>
                        <span className="flex items-center gap-1">Location <SortIcon field="location" /></span>
                      </th>
                      <th className={thClass} onClick={() => handleSort('experience')}>
                        <span className="flex items-center gap-1">Exp. <SortIcon field="experience" /></span>
                      </th>
                      <th className={thClass} onClick={() => handleSort('rate')}>
                        <span className="flex items-center gap-1">Rate <SortIcon field="rate" /></span>
                      </th>
                      <th className={thClass} onClick={() => handleSort('availability')}>
                        <span className="flex items-center gap-1">Status <SortIcon field="availability" /></span>
                      </th>
                      <th className={thClass} onClick={() => handleSort('assignment')}>
                        <span className="flex items-center gap-1">Assignment <SortIcon field="assignment" /></span>
                      </th>
                      <th className={thClass} onClick={() => handleSort('joined')}>
                        <span className="flex items-center gap-1">Joined <SortIcon field="joined" /></span>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredConsultants.map((consultant) => {
                      const isExpanded = expandedRow === consultant.id;
                      const isPaused = consultant.account_status?.status === 'paused';
                      const isSoftDeleted = !!consultant.soft_deleted_at;
                      const name = consultant.full_name || `${consultant.first_name} ${consultant.last_name}`;
                      const rate = consultant.hourly_rate
                        ? (consultant.hourly_rate.toString().startsWith('$') ? consultant.hourly_rate : `$${consultant.hourly_rate}`)
                        : '—';

                      return (
                        <React.Fragment key={consultant.id}>
                          <tr
                            className={`hover:bg-gray-50 transition-colors cursor-pointer ${isSoftDeleted ? 'bg-red-50/40 opacity-60' : isPaused ? 'opacity-60' : ''}`}
                            onClick={() => {
                              const next = isExpanded ? null : consultant.id;
                              setExpandedRow(next);
                              if (next) fetchConsultantStats(next);
                            }}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2.5">
                                {(consultant.photo_url || consultant.profile_photo_url) ? (
                                  <img
                                    src={consultant.photo_url || consultant.profile_photo_url || ''}
                                    alt={name}
                                    className="w-8 h-8 rounded-full object-cover shrink-0"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      const fb = e.currentTarget.nextElementSibling as HTMLElement;
                                      if (fb) fb.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="w-8 h-8 rounded-full bg-[#40E0D0]/10 flex items-center justify-center shrink-0"
                                  style={{ display: (consultant.photo_url || consultant.profile_photo_url) ? 'none' : 'flex' }}
                                >
                                  <UserCheck className="h-4 w-4 text-[#40E0D0]" />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">{name}</p>
                                  {consultant.title && <p className="text-xs text-gray-500">{consultant.title}</p>}
                                  {isSoftDeleted && <span className="text-xs text-red-600 font-medium bg-red-50 px-1.5 py-0.5 rounded">Soft Deleted</span>}
                                  {!isSoftDeleted && isPaused && <span className="text-xs text-orange-500 font-medium">Paused</span>}
                                  {consultant.deletion_request?.status === 'pending' && (
                                    <span className="text-xs text-red-700 font-semibold bg-red-100 border border-red-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                                      <Trash2 className="h-3 w-3" />
                                      Delete Requested
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{consultant.email}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{consultant.country_region || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                              {consultant.experience_years ? `${consultant.experience_years} yr${parseInt(consultant.experience_years) !== 1 ? 's' : ''}` : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{rate}</td>
                            <td className="px-4 py-3">{availabilityBadge(consultant)}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {consultant.active_assignment ? (
                                <div>
                                  <p className="font-medium text-gray-900">{consultant.active_assignment.company_name || '—'}</p>
                                  <p className="text-xs text-gray-500">{consultant.active_assignment.client_name || ''}</p>
                                </div>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {consultant.created_at ? new Date(consultant.created_at).toLocaleDateString() : '—'}
                            </td>
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              <div className="relative" ref={openDropdown === consultant.id ? dropdownRef : null}>
                                <button
                                  onClick={(e) => {
                                    const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                                    if (openDropdown === consultant.id) {
                                      setOpenDropdown(null);
                                      setDropdownPos(null);
                                    } else {
                                      setOpenDropdown(consultant.id);
                                      setDropdownPos({ top: rect.bottom + window.scrollY + 4, right: window.innerWidth - rect.right });
                                    }
                                  }}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                                  title="Actions"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </button>
                                {openDropdown === consultant.id && dropdownPos && (
                                  <div
                                    style={{ position: 'fixed', top: dropdownPos.top, right: dropdownPos.right, zIndex: 9999 }}
                                    className="w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 divide-y divide-gray-50"
                                  >
                                    <div className="py-1">
                                      <button
                                        onClick={() => { openEditModal(consultant); setOpenDropdown(null); }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                      >
                                        <Edit className="h-4 w-4 text-gray-400" />
                                        Edit Profile
                                      </button>
                                      <button
                                        onClick={() => { handlePauseAccount(consultant.id, consultant.account_status?.status || 'active'); setOpenDropdown(null); }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                      >
                                        {isPaused ? <Play className="h-4 w-4 text-green-500" /> : <Pause className="h-4 w-4 text-yellow-500" />}
                                        {isPaused ? 'Activate Account' : 'Pause Account'}
                                      </button>
                                      <button
                                        onClick={() => { handleSendPasswordReset(consultant.email); setOpenDropdown(null); }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                      >
                                        <Key className="h-4 w-4 text-[#40E0D0]" />
                                        Send Password Reset
                                      </button>
                                      {!consultant.active_assignment ? (
                                        <button
                                          onClick={() => { openAssignModal(consultant); setOpenDropdown(null); }}
                                          className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                          <UserPlus className="h-4 w-4 text-blue-500" />
                                          Assign to Client
                                        </button>
                                      ) : (
                                        <button
                                          onClick={() => { handleRemoveAssignment(consultant.active_assignment!.id); setOpenDropdown(null); }}
                                          className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                          <X className="h-4 w-4 text-orange-500" />
                                          End Assignment
                                        </button>
                                      )}
                                    </div>
                                    <div className="py-1">
                                      {consultant.soft_deleted_at ? (
                                        <button
                                          onClick={() => { setConfirmModal({ type: 'restore', consultant }); setOpenDropdown(null); }}
                                          className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors"
                                        >
                                          <RotateCcw className="h-4 w-4" />
                                          Restore Account
                                        </button>
                                      ) : (
                                        <button
                                          onClick={() => { setConfirmModal({ type: 'soft_delete', consultant }); setOpenDropdown(null); }}
                                          className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-orange-700 hover:bg-orange-50 transition-colors"
                                        >
                                          <Trash className="h-4 w-4" />
                                          Soft Delete (30-day hold)
                                        </button>
                                      )}
                                      <button
                                        onClick={() => { setConfirmModal({ type: 'hard_delete', consultant }); setOpenDropdown(null); }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        Hard Delete (Permanent)
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr className="bg-gray-50/80">
                              <td colSpan={9} className="px-6 py-5">
                                {(() => {
                                  const pct = computeConsultantCompleteness(consultant);
                                  const stats = consultantStats[consultant.id];
                                  const isLoadingStats = loadingStats[consultant.id];
                                  return (
                                    <div className="space-y-5">

                                      {/* Header: photo + name + key info */}
                                      <div className="flex items-start gap-5 p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                                        <div className="shrink-0">
                                          {(consultant.photo_url || consultant.profile_photo_url) ? (
                                            <img
                                              src={consultant.photo_url || consultant.profile_photo_url || ''}
                                              alt={name}
                                              className="w-20 h-20 rounded-xl object-cover border-2 border-gray-100"
                                            />
                                          ) : (
                                            <div className="w-20 h-20 rounded-xl bg-[#40E0D0]/10 flex items-center justify-center border-2 border-[#40E0D0]/20">
                                              <UserCheck className="h-9 w-9 text-[#40E0D0]" />
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <h3 className="text-base font-bold text-gray-900">{name}</h3>
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${consultant.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                              {consultant.status || 'pending'}
                                            </span>
                                            {isSoftDeleted && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Soft Deleted</span>}
                                            {isPaused && !isSoftDeleted && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">Paused</span>}
                                          </div>
                                          <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-3">
                                            <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{consultant.email}</span>
                                            {consultant.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{consultant.phone}</span>}
                                            {consultant.country_region && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{consultant.country_region}</span>}
                                            {consultant.title && <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{consultant.title}</span>}
                                            {consultant.hourly_rate && <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />{consultant.hourly_rate.toString().startsWith('$') ? consultant.hourly_rate : `$${consultant.hourly_rate}`}/hr</span>}
                                          </div>
                                          <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                                            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Joined: {new Date(consultant.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                            {stats?.lastActive && <span className="flex items-center gap-1"><Activity className="h-3 w-3" />Last login: {new Date(stats.lastActive).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>}
                                          </div>
                                        </div>
                                        {/* Profile completion */}
                                        <div className="shrink-0 text-center w-28">
                                          <div className="relative w-16 h-16 mx-auto mb-1">
                                            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                                              <circle cx="32" cy="32" r="26" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                                              <circle
                                                cx="32" cy="32" r="26" fill="none"
                                                stroke={pct === 100 ? '#10b981' : pct >= 90 ? '#f59e0b' : '#ef4444'}
                                                strokeWidth="6"
                                                strokeDasharray={`${(pct / 100) * 163.4} 163.4`}
                                                strokeLinecap="round"
                                              />
                                            </svg>
                                            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-800">{pct}%</span>
                                          </div>
                                          <p className="text-xs text-gray-500 font-medium">Profile Complete</p>
                                          {pct < 90 && <p className="text-xs text-red-500 mt-0.5">Needs update</p>}
                                          {pct >= 90 && pct < 100 && <p className="text-xs text-amber-500 mt-0.5">Interview-eligible</p>}
                                          {pct === 100 && <p className="text-xs text-emerald-500 mt-0.5">Fully complete</p>}
                                        </div>
                                      </div>

                                      {/* Stats grid: community activity */}
                                      <div>
                                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Community Activity</p>
                                        {isLoadingStats ? (
                                          <div className="text-xs text-gray-400 py-2">Loading activity stats...</div>
                                        ) : stats ? (
                                          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                                            <div className="bg-white rounded-lg border border-gray-100 p-3 text-center shadow-sm">
                                              <div className="flex items-center justify-center gap-1 mb-1">
                                                <Globe className="h-3.5 w-3.5 text-blue-400" />
                                                <span className="text-xs text-gray-500">Public Posts</span>
                                              </div>
                                              <p className="text-xl font-bold text-gray-800">{stats.publicPosts}</p>
                                            </div>
                                            <div className="bg-white rounded-lg border border-gray-100 p-3 text-center shadow-sm">
                                              <div className="flex items-center justify-center gap-1 mb-1">
                                                <Lock className="h-3.5 w-3.5 text-gray-400" />
                                                <span className="text-xs text-gray-500">Private Posts</span>
                                              </div>
                                              <p className="text-xl font-bold text-gray-800">{stats.privatePosts}</p>
                                            </div>
                                            {stats.hiddenPosts > 0 && (
                                              <div className="bg-white rounded-lg border border-red-100 p-3 text-center shadow-sm">
                                                <div className="flex items-center justify-center gap-1 mb-1">
                                                  <Eye className="h-3.5 w-3.5 text-red-400" />
                                                  <span className="text-xs text-gray-500">Hidden Posts</span>
                                                </div>
                                                <p className="text-xl font-bold text-red-600">{stats.hiddenPosts}</p>
                                              </div>
                                            )}
                                            <div className="bg-white rounded-lg border border-gray-100 p-3 text-center shadow-sm">
                                              <div className="flex items-center justify-center gap-1 mb-1">
                                                <Heart className="h-3.5 w-3.5 text-rose-400" />
                                                <span className="text-xs text-gray-500">Total Likes</span>
                                              </div>
                                              <p className="text-xl font-bold text-gray-800">{stats.totalLikes}</p>
                                            </div>
                                            <div className="bg-white rounded-lg border border-gray-100 p-3 text-center shadow-sm">
                                              <div className="flex items-center justify-center gap-1 mb-1">
                                                <Users className="h-3.5 w-3.5 text-[#40E0D0]" />
                                                <span className="text-xs text-gray-500">Followers</span>
                                              </div>
                                              <p className="text-xl font-bold text-gray-800">{stats.totalFollowers}</p>
                                              <p className="text-xs text-gray-400">{stats.totalFollowing} following</p>
                                            </div>
                                            <div className="bg-white rounded-lg border border-gray-100 p-3 text-center shadow-sm">
                                              <div className="flex items-center justify-center gap-1 mb-1">
                                                <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
                                                <span className="text-xs text-gray-500">Connections</span>
                                              </div>
                                              <p className="text-xl font-bold text-gray-800">{stats.totalConnections}</p>
                                              {stats.pendingConnections > 0 && <p className="text-xs text-amber-500">{stats.pendingConnections} pending</p>}
                                            </div>
                                            <div className="bg-white rounded-lg border border-gray-100 p-3 text-center shadow-sm">
                                              <div className="flex items-center justify-center gap-1 mb-1">
                                                <MessageSquare className="h-3.5 w-3.5 text-blue-400" />
                                                <span className="text-xs text-gray-500">Group Chats</span>
                                              </div>
                                              <p className="text-xl font-bold text-gray-800">{stats.groupChats}</p>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="text-xs text-gray-400 py-2">No activity data available.</div>
                                        )}
                                      </div>

                                      {/* Profile fields */}
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                        {consultant.workday_modules && (
                                          <div className="bg-white rounded-lg border border-gray-100 p-3 shadow-sm">
                                            <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Workday Modules</p>
                                            <p className="text-gray-700">{consultant.workday_modules}</p>
                                          </div>
                                        )}
                                        {consultant.certifications && (
                                          <div className="bg-white rounded-lg border border-gray-100 p-3 shadow-sm">
                                            <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Certifications</p>
                                            <p className="text-gray-700">{consultant.certifications}</p>
                                          </div>
                                        )}
                                        {consultant.experience_years && (
                                          <div className="bg-white rounded-lg border border-gray-100 p-3 shadow-sm">
                                            <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Experience</p>
                                            <p className="text-gray-700">{consultant.experience_years} years</p>
                                          </div>
                                        )}
                                        {consultant.message && (
                                          <div className="sm:col-span-2 lg:col-span-3 bg-white rounded-lg border border-gray-100 p-3 shadow-sm">
                                            <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Bio / Message</p>
                                            <p className="text-gray-700 whitespace-pre-line">{consultant.message}</p>
                                          </div>
                                        )}
                                      </div>

                                      {/* Links row */}
                                      <div className="flex flex-wrap gap-3">
                                        {consultant.resume_url && (
                                          <a href={consultant.resume_url} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-[#40E0D0] hover:bg-gray-50 shadow-sm transition-colors"
                                            onClick={e => e.stopPropagation()}>
                                            <FileText className="h-3.5 w-3.5" /> View Resume
                                          </a>
                                        )}
                                        {consultant.active_assignment && (
                                          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700 shadow-sm">
                                            <Briefcase className="h-3.5 w-3.5" />
                                            <span>On assignment: <strong>{consultant.active_assignment.client_name || 'Client'}</strong></span>
                                            <span className="text-blue-400">since {new Date(consultant.active_assignment.start_date).toLocaleDateString()}</span>
                                            {consultant.active_assignment.contract_url && (
                                              <a href={consultant.active_assignment.contract_url} target="_blank" rel="noopener noreferrer"
                                                className="underline hover:text-blue-900 ml-1"
                                                onClick={e => e.stopPropagation()}>
                                                Contract
                                              </a>
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      {/* Deletion request */}
                                      {consultant.deletion_request?.status === 'pending' && (
                                        <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                                          <p className="text-xs font-semibold text-red-500 uppercase mb-2">Account Deletion Request</p>
                                          <div className="flex flex-wrap items-center gap-4 text-sm">
                                            <span className="text-red-700">Requested: {new Date(consultant.deletion_request.requested_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                            {consultant.deletion_request.reason && <span className="text-red-700">Reason: {consultant.deletion_request.reason}</span>}
                                            <div className="ml-auto flex gap-2">
                                              <button
                                                onClick={(e) => { e.stopPropagation(); setConfirmModal({ type: 'soft_delete', consultant }); }}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors"
                                              >
                                                <Trash2 className="h-3.5 w-3.5" /> Process Deletion
                                              </button>
                                              <button
                                                onClick={async (e) => {
                                                  e.stopPropagation();
                                                  await supabase.from('account_deletion_requests').update({ status: 'dismissed', resolved_at: new Date().toISOString() }).eq('id', consultant.deletion_request!.id);
                                                  showMessage('success', 'Deletion request dismissed');
                                                  fetchConsultants();
                                                }}
                                                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                                              >
                                                Dismiss
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>}
        </div>
      </div>

      {confirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            {confirmModal.type === 'soft_delete' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Trash className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Soft Delete Account</h3>
                    <p className="text-sm text-gray-500">This action can be reversed within 30 days</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-2">
                  You are about to soft delete <span className="font-semibold text-gray-900">{confirmModal.consultant.full_name || `${confirmModal.consultant.first_name} ${confirmModal.consultant.last_name}`}</span>.
                </p>
                <ul className="text-sm text-gray-500 space-y-1 mb-6 list-disc list-inside bg-orange-50 rounded-lg p-3">
                  <li>The user will immediately lose login access</li>
                  <li>Their data will be held in the database for 30 days</li>
                  <li>Use "Restore Account" to reverse this action</li>
                </ul>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setConfirmModal(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm hover:bg-gray-50 transition-colors">Cancel</button>
                  <button
                    onClick={() => handleSoftDelete(confirmModal.consultant)}
                    disabled={processing}
                    className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-colors disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Soft Delete'}
                  </button>
                </div>
              </>
            )}
            {confirmModal.type === 'hard_delete' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Permanently Delete Account</h3>
                    <p className="text-sm text-red-500 font-medium">This cannot be undone</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  Deleting <span className="font-semibold text-gray-900">{confirmModal.consultant.full_name || `${confirmModal.consultant.first_name} ${confirmModal.consultant.last_name}`}</span> will remove their login access and profile.
                </p>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-3">
                  <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Permanently removed</p>
                  <ul className="text-sm text-red-700 space-y-0.5 list-disc list-inside">
                    <li>Login account &amp; profile</li>
                    <li>Assignments &amp; onboarding records</li>
                  </ul>
                </div>
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 mb-6">
                  <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">Preserved for your records</p>
                  <ul className="text-sm text-green-700 space-y-0.5 list-disc list-inside">
                    <li>Login &amp; IP address history</li>
                  </ul>
                </div>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setConfirmModal(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm hover:bg-gray-50 transition-colors">Cancel</button>
                  <button
                    onClick={() => handleHardDelete(confirmModal.consultant)}
                    disabled={processing}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {processing ? 'Deleting...' : 'Permanently Delete'}
                  </button>
                </div>
              </>
            )}
            {confirmModal.type === 'restore' && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <RotateCcw className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Restore Account</h3>
                    <p className="text-sm text-gray-500">Re-enable login access</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-6">
                  Restoring <span className="font-semibold text-gray-900">{confirmModal.consultant.full_name || `${confirmModal.consultant.first_name} ${confirmModal.consultant.last_name}`}</span> will re-enable their login access and remove the deletion hold.
                </p>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setConfirmModal(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm hover:bg-gray-50 transition-colors">Cancel</button>
                  <button
                    onClick={() => handleRestore(confirmModal.consultant)}
                    disabled={processing}
                    className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {processing ? 'Restoring...' : 'Restore Account'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Assign Consultant to Client</h2>
              <button onClick={() => setShowAssignModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Consultant</label>
                <input type="text" value={assignmentData.consultant_name} disabled className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Client *</label>
                <select value={assignmentData.client_id} onChange={(e) => setAssignmentData({ ...assignmentData, client_id: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" required>
                  <option value="">Choose a client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.full_name} ({c.email})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                <input type="date" value={assignmentData.start_date} onChange={(e) => setAssignmentData({ ...assignmentData, start_date: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hourly Rate</label>
                <input type="number" step="0.01" value={assignmentData.hourly_rate} onChange={(e) => setAssignmentData({ ...assignmentData, hourly_rate: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" placeholder="150.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Signed Contract</label>
                <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setAssignmentData({ ...assignmentData, contract_file: e.target.files?.[0] || null })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea value={assignmentData.notes} onChange={(e) => setAssignmentData({ ...assignmentData, notes: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" rows={3} placeholder="Add any notes about this assignment..." />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowAssignModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-900" disabled={processing}>Cancel</button>
              <button onClick={handleCreateAssignment} disabled={processing} className="px-4 py-2 bg-[#40E0D0] text-white rounded-lg hover:bg-[#40E0D0]/90 disabled:opacity-50 flex items-center gap-2">
                {processing ? 'Processing...' : <><UserPlus className="h-4 w-4" /> Create Assignment</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedConsultant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Edit Consultant Profile</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input type="text" value={editData.first_name || ''} onChange={(e) => setEditData({ ...editData, first_name: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input type="text" value={editData.last_name || ''} onChange={(e) => setEditData({ ...editData, last_name: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input type="email" value={editData.email || ''} onChange={(e) => setEditData({ ...editData, email: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input type="text" value={editData.phone || ''} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input type="text" value={editData.country_region || ''} onChange={(e) => setEditData({ ...editData, country_region: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hourly Rate</label>
                  <input type="text" value={editData.hourly_rate || ''} onChange={(e) => setEditData({ ...editData, hourly_rate: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Availability</label>
                  <select value={editData.availability || ''} onChange={(e) => setEditData({ ...editData, availability: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white">
                    <option value="available">Available</option>
                    <option value="busy">Busy</option>
                    <option value="unavailable">Unavailable</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message / Bio</label>
                <textarea value={editData.message || ''} onChange={(e) => setEditData({ ...editData, message: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] text-gray-900 bg-white" rows={4} />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowEditModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-900" disabled={processing}>Cancel</button>
              <button onClick={handleUpdateConsultant} disabled={processing} className="px-4 py-2 bg-[#40E0D0] text-white rounded-lg hover:bg-[#40E0D0]/90 disabled:opacity-50">
                {processing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
